import './styles.scss'

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingOutlined } from '@ant-design/icons'
import { Col, Form, Input, Modal, Row } from 'antd'
import clsx from 'clsx'

import B2becTranslation from '@/components/B2becTranslation'
import { AlertIcon } from '@/components/Icons'
import ImageWithFallBack from '@/components/ImageWithFallback'
import DangerouslySetInnerElement from '@/components/SpecialSymbolElement'
import { customerServiceCaseService, ProductService } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectServiceRepairPageByLanguage } from '@/store/selectors/configSelector'
import { getSelectedMachineData } from '@/store/selectors/serviceCaseSelector'
import { selectUserCountryId } from '@/store/selectors/userSelector'
import { resetSelectedMachineInfo, setSelectedMachineInfo } from '@/store/slices/serviceCaseSlice'
import useAsync from '@/hooks/useAsync'
import useDebounce from '@/hooks/useDebounce'
import useDeviceDetect from '@/hooks/useDeviceDetect'
import { ASYNC_STATUS, INVALID_TYPES } from '@/libs/constants'
import { validateSerialNumber } from '@/libs/utils/formatSerialNumber'
import { formatMaterialID } from '@/libs/utils/material-number'
import greenCheckIconImage from '@/assets/img/green-check-icon.webp'
import itemNumberExampleImage from '@/assets/img/item-number-example.webp'
import moreInfoIconImage from '@/assets/img/more-info-icon.webp'
import serialNumberExampleImage from '@/assets/img/serial-number-example.webp'

const SERIAL_NUMBER_VALIDATE_MESSAGE = {
  WRONG_SERIAL_NUMBER: 'service.selectMachine.serialNumber.wrongFormat',
  WRONG_SERIAL_NUMBER_WITH_LETTER: 'service.selectMachine.serialNumber.wrongFormatWithLeter',
}

const validateExcludedMachine = async (materialNumber, countryId) => {
  return customerServiceCaseService.validateMaterialNumber({
    materialNumber,
    countryId,
  })
}

interface SelectNewMachineBlockProps {
  selectedMachineData: {
    isExcludedMachine?: boolean
  }
}

interface LinkToExternalPageProps {
  children?: React.ReactNode
}

const LinkToExternalPage = ({ children }: LinkToExternalPageProps) => {
  const serviceRepairPage = useAppSelector(selectServiceRepairPageByLanguage)

  return (
    <a
      className="select-machine__excluded-machine__redirect-link"
      href={serviceRepairPage}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </a>
  )
}

const SelectNewMachineBlock = (props: SelectNewMachineBlockProps) => {
  const {
    selectedMachineData: { isExcludedMachine },
  } = props

  const { isMobile } = useDeviceDetect()

  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [isShowModal, setShowInfoModal] = useState(false)

  const selectedMachineData = useAppSelector(getSelectedMachineData)
  const countryId = useAppSelector(selectUserCountryId)

  const [serialNumber, setSerialNumber] = useState(null)
  const [serialNumberMessage, setSerialNumberMessage] = useState(null)
  const [isSerialNumberInfoShown, setIsSerialNumberInfoShown] = useState(false)

  const [itemNumber, setItemNumber] = useState(null)
  const [itemNumberMessage, setItemNumberMessage] = useState(null)
  const [isItemNumberInfoShown, setIsItemNumberInfoShown] = useState(false)

  const [itemName, setItemName] = useState(null)
  const [itemImg, setItemImg] = useState(null)

  const debouncedGetBasicProduct = useDebounce(itemNumber, 200)

  const submitGetBasicProduct = useCallback(async (materialNumber) => {
    return ProductService.getBasicProductInfo(materialNumber)
  }, [])

  const {
    execute: executeExcludedMachineValidation,
    status: excludedMachineValidationStatus,
    error: excludedMachineValidationError,
  } = useAsync(validateExcludedMachine, false)

  const {
    execute: executeGetBasicProductInfo,
    status: getBasicProductStatus,
    error: getBasicProductError,
  } = useAsync(submitGetBasicProduct, false)

  const clearData = useCallback(() => {
    // clear item number
    setItemNumber(null)
    setItemNumberMessage(null)
    setIsItemNumberInfoShown(false)
    // clear item info
    setItemName(null)
    setItemImg(null)
    // clear serial number
    setSerialNumber(null)
    setSerialNumberMessage(null)
    setIsSerialNumberInfoShown(false)
  }, [])

  // when changing service, reset the local state
  useEffect(() => {
    if (
      selectedMachineData?.isChangedService
      && selectedMachineData?.serviceEntry !== null
    ) {
      clearData()
    }
  }, [clearData, selectedMachineData.isChangedService, selectedMachineData.serviceEntry])

  useEffect(() => {
    if (getBasicProductStatus === ASYNC_STATUS.PENDING) {
      setItemNumberMessage(t('form.placeHolder.loading'))
      setItemName(null)
      setItemImg(null)
    }
    if (getBasicProductError) {
      setItemNumberMessage(t('service.selectMachine.itemNumber.notExisted'))
      setItemName(null)
      setItemImg(null)
      // setMachineInformation({});
      dispatch(resetSelectedMachineInfo())
    }
  }, [dispatch, getBasicProductStatus, getBasicProductError, t])

  useEffect(() => {
    if (excludedMachineValidationStatus === ASYNC_STATUS.ERROR) {
      if (
        excludedMachineValidationError?.response?.data
          === INVALID_TYPES.EXCLUDED
      ) {
        dispatch(
          setSelectedMachineInfo({
            isExcludedMachine: true,
            validateStatus: ASYNC_STATUS.ERROR,
          }),
        )
      }
    } else {
      dispatch(
        setSelectedMachineInfo({
          isExcludedMachine: false,
          validateStatus: excludedMachineValidationStatus,
        }),
      )
    }
  }, [dispatch, excludedMachineValidationStatus, excludedMachineValidationError])

  const onSerialNumberChange = async (e) => {
    const value = e.target.value.trim()
    try {
      const serial = await validateSerialNumber(value)
      dispatch(setSelectedMachineInfo({ serialNumber: serial }))
      setSerialNumberMessage(null)
      setSerialNumber(serial)
    } catch (error) {
      const errorMessageKey = SERIAL_NUMBER_VALIDATE_MESSAGE[error]
      setSerialNumberMessage(t(errorMessageKey))
    }
  }

  const handleInputValue = (string_) => {
    if (string_.length <= 8 && /^\d{8}$/.test(string_)) {
      const formattedString = formatMaterialID(string_)
      return formattedString
    }
    return string_
  }

  const isItemNumberValid = useCallback((string_) => {
    return string_.length === 11 && /^\d\.\d{3}-\d{3}\.\d$/.test(string_)
  }, [])

  const getAndValidateProduct = useCallback(
    async (_materialNumber: string) => {
      const getProduct = executeGetBasicProductInfo(_materialNumber).then(
        ({ response }) => {
          if (response?.data) {
            const { materialNumber, materialName, pictureUrl } = response?.data
            setItemName(materialName)
            setItemImg(pictureUrl)
            setItemNumberMessage(null)

            dispatch(
              setSelectedMachineInfo({
                id: materialNumber,
                name: materialName,
                image: pictureUrl,
              }),
            )
          }
        },
      )

      const validateProduct = executeExcludedMachineValidation(_materialNumber, countryId)

      await Promise.all([getProduct, validateProduct])
    },
    [dispatch, countryId, executeExcludedMachineValidation, executeGetBasicProductInfo],
  )

  useEffect(() => {
    if (debouncedGetBasicProduct) {
      if (debouncedGetBasicProduct.length <= 11) {
        if (isItemNumberValid(debouncedGetBasicProduct)) {
          getAndValidateProduct(debouncedGetBasicProduct)
        } else {
          setItemNumberMessage(t('errors.stringFormat'))
          setItemName(null)
          setItemImg(null)
        }
      } else {
        setItemNumberMessage(t('errors.maxInputExceeded'))
        setItemName(null)
        setItemImg(null)
      }
    } else {
      clearData()

      dispatch(resetSelectedMachineInfo())
    }
  }, [debouncedGetBasicProduct, getAndValidateProduct, t, isItemNumberValid, dispatch, clearData])

  const renderItemNumberBlock = () => {
    const renderItemNumberGreenCheck = itemNumberMessage == null
        && itemNumber?.length > 0
        && isItemNumberValid(itemNumber)
      ? (
        <div className="select-machine__left__serial-number__green-check">
          <ImageWithFallBack src={greenCheckIconImage} />
        </div>
      )
      : null
    return (
      <div className="select-machine__left__item-number">
        <span className="select-machine__label">{t('service.selectMachine.itemNumber.label')}</span>
        <div className="select-machine__left__item-number__wrapper">
          <Input
            autoComplete="off"
            className="custom-input select-machine__left__item-number__input"
            placeholder={t('service.selectMachine.itemNumber.placeHolder')}
            value={itemNumber}
            onChange={(e) => {
              const value = e.target.value.trim()
              const re = /^[\d, .-]+$/
              if (value !== '' && re.test(value)) {
                const formattedValue = handleInputValue(value)
                setItemNumber(formattedValue)
              } else {
                setItemNumber(value.replaceAll(/[^\d, .-]/g, ''))
              }
            }}
          />

          {excludedMachineValidationStatus === ASYNC_STATUS.PENDING
            ? (
              <div className="select-machine__left__serial-number__alert">
                <LoadingOutlined />
              </div>
            )
            : isExcludedMachine
            ? (
              <div className="select-machine__left__serial-number__alert">
                <AlertIcon />
              </div>
            )
            : renderItemNumberGreenCheck}
          <button
            className="select-machine__left__more-info-button"
            onClick={() => {
              setIsItemNumberInfoShown(!isItemNumberInfoShown)
            }}
          >
            <ImageWithFallBack className="select-machine__more-info" src={moreInfoIconImage} />
          </button>
        </div>
        <p className="select-machine__error">{itemNumberMessage}</p>
      </div>
    )
  }

  const renderExcludedMachineInfo = useCallback(() => {
    const renderConditions = isExcludedMachine
      && excludedMachineValidationError?.response?.data === INVALID_TYPES.EXCLUDED

    return (
      renderConditions && (
        <div className="select-machine__excluded-machine__messages">
          <B2becTranslation
            isComponent
            value="service.selectMachine.excludedMachineMessages"
            htmlTags={[
              <div key="conditionEmpty" />,
              <LinkToExternalPage key="conditionExternalPage" />,
            ]}
          />
        </div>
      )
    )
  }, [isExcludedMachine, excludedMachineValidationError])

  const renderNameBlock = () => {
    return (
      <div className="select-machine__left__name">
        <span className="select-machine__label">
          {t('service.selectMachine.generateName.label')}
        </span>
        {isMobile && itemImg
          ? (
            <div className="select-machine__left__name__block">
              <ImageWithFallBack
                alt={itemName}
                height="50px"
                src={itemImg}
                width="50px"
              />
              <span>{itemName}</span>
            </div>
          )
          : (
            <Input
              disabled
              className="custom-input select-machine__left__name__input"
              type="text"
              value={itemName}
            />
          )}
      </div>
    )
  }

  const renderSerialNumberBlock = () => {
    const renderSerialNumberGreenCheck = serialNumberMessage == null && serialNumber?.length > 0
      ? (
        <div className="select-machine__left__serial-number__green-check">
          <ImageWithFallBack src={greenCheckIconImage} />
        </div>
      )
      : null
    return (
      <div className="select-machine__left__serial-number">
        <span className="select-machine__label">
          {t('service.selectMachine.serialNumber.label')}
        </span>
        <div className="select-machine__left__serial-number__wrapper">
          <Form.Item
            noStyle
            name={['serialNumber']}
            rules={[
              {
                validator: async (_, value) => {
                  return validateSerialNumber(
                    value,
                    // t("service.selectMachine.serialNumber.wrongFormat"),
                  )
                },
              },
            ]}
          >
            <Input
              autoComplete="off"
              className="custom-input select-machine__left__serial-number__input"
              placeholder={t('service.selectMachine.serialNumber.placeHolder')}
              onChange={onSerialNumberChange}
            />
          </Form.Item>
          {renderSerialNumberGreenCheck}
          <button
            className="select-machine__left__more-info-button"
            onClick={() => {
              setIsSerialNumberInfoShown(!isSerialNumberInfoShown)
            }}
          >
            <ImageWithFallBack className="select-machine__more-info" src={moreInfoIconImage} />
          </button>
        </div>
        <p className="select-machine__error">{serialNumberMessage}</p>
        {!isMobile && (
          <div className="select-machine__left__image">
            <img alt={itemName} src={itemImg} style={{ width: '100%' }} />
          </div>
        )}
      </div>
    )
  }

  const renderLeftBlock = () => {
    return (
      <>
        {renderItemNumberBlock()}
        {isMobile && renderExcludedMachineInfo()}
        {renderNameBlock()}
        {renderSerialNumberBlock()}
      </>
    )
  }

  const renderItemNumberInfo = () => {
    return (
      <div
        className={clsx('select-machine__right__block', {
          'select-machine__right__block--visible': isItemNumberInfoShown,
        })}
      >
        <div className="select-machine__right__image">
          <ImageWithFallBack src={itemNumberExampleImage} />
        </div>
        <div className="select-machine__right__main-content select-machine__right__content__description">
          <span style={{ marginBottom: 30, display: 'block' }}>
            {t('service.selectMachine.itemNumberInfo.title')}
          </span>
          <p>{t('service.selectMachine.itemNumberInfo.description')}</p>
        </div>
      </div>
    )
  }

  const renderSerialNumberInfo = () => {
    return (
      <div
        className={clsx('select-machine__right__block', {
          'select-machine__right__block--visible': isSerialNumberInfoShown,
        })}
      >
        <div className="select-machine__right__image">
          <ImageWithFallBack src={serialNumberExampleImage} />
        </div>
        <div className="select-machine__right__main-content select-machine__right__content__description">
          <span style={{ marginBottom: 30, display: 'block' }}>
            {t('service.selectMachine.serialNumberInfo.title')}
          </span>
          <p>{t('service.selectMachine.serialNumberInfo.description')}</p>
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (isMobile && (isItemNumberInfoShown || isSerialNumberInfoShown)) {
      setShowInfoModal(true)
    }
  }, [isMobile, isItemNumberInfoShown, isSerialNumberInfoShown])

  const renderInstructionModal = useCallback(
    ({ modalTitle, modalContent, imgSrc }) => {
      const onCloseModal = () => {
        // close modal
        setShowInfoModal(false)
        // because the info block on desktop is shown through the toggle feature
        // on mobile, close the modal is not trigger the toggle again
        // need to set it manually
        setIsSerialNumberInfoShown(false)
        setIsItemNumberInfoShown(false)
      }

      return (
        <Modal
          footer={null}
          open={isShowModal}
          title={modalTitle}
          wrapClassName="info-modal--wrapper"
          onCancel={onCloseModal}
        >
          <DangerouslySetInnerElement rawHTML={modalContent} />
          <div>
            <ImageWithFallBack src={imgSrc} />
          </div>
        </Modal>
      )
    },
    [isShowModal],
  )

  const renderRightBlock = () => {
    return (
      <div className="instruction ">
        {!isMobile && renderExcludedMachineInfo()}
        {isMobile
          ? (
            <>
              {isItemNumberInfoShown
                && renderInstructionModal({
                  modalTitle: t('service.selectMachine.itemNumberInfo.title'),
                  modalContent: t(
                    'service.selectMachine.itemNumberInfo.description',
                  ),
                  imgSrc: itemNumberExampleImage,
                })}
              {isSerialNumberInfoShown
                && renderInstructionModal({
                  modalTitle: t('service.selectMachine.serialNumberInfo.title'),
                  modalContent: t(
                    'service.selectMachine.serialNumberInfo.description',
                  ),
                  imgSrc: serialNumberExampleImage,
                })}
            </>
          )
          : (
            <>
              {renderItemNumberInfo()}
              {renderSerialNumberInfo()}
            </>
          )}
      </div>
    )
  }

  return (
    <div className="select-machine__wrapper">
      <span className="select-machine__description">
        {t('service.selectMachine.findMachineLabel')}
      </span>
      <Row gutter={[100, 0]}>
        <Col className="select-machine__left" lg={10} xs={24}>
          {renderLeftBlock()}
        </Col>
        <Col className="select-machine__right" lg={14} xs={24}>
          {renderRightBlock()}
        </Col>
      </Row>
    </div>
  )
}

export default SelectNewMachineBlock
